/**
 * Pass 1 Schema — Sprint 17c
 *
 * Zod validation for structured Pass 1 LLM output.
 * Pass 1 now returns per-signal intent classifications (not free-form text).
 */
import { z } from 'zod';
import { extractJSON } from './pass2-schema';
export const Pass1SignalSchema = z.object({
    description: z.string().min(1),
    intentLevel: z.number().int().min(1).max(5),
    intentReason: z.string().min(1),
    entity: z.string().min(1),
    proxy: z.enum(['attention', 'investment', 'communication', 'repetition', 'initiative', 'priority']),
    source: z.enum(['financial', 'calendar', 'email', 'browsing']),
});
export const Pass1ResponseSchema = z.object({
    signals: z.array(Pass1SignalSchema).min(1),
    underrepresentedSignals: z.string().optional(),
});
/**
 * Intent Commitment Ladder — classified by the LLM, not regex.
 *
 * | Level | Name               | Description                                          |
 * |-------|--------------------|------------------------------------------------------|
 * |   5   | Completed Action   | Purchase, booking, payment, registration             |
 * |   4   | Active Research    | Comparison shopping, review reading, price checking  |
 * |   3   | Expressed Interest | Self-created event, forwarded link, reply chain      |
 * |   2   | Passive Engagement | Opened email, visited page, read article             |
 * |   1   | Ambient Exposure   | Newsletter, notification, automated email            |
 */
export const INTENT_LADDER = [
    { level: 5, name: 'Completed Action', description: 'Purchase, booking, payment, registration' },
    { level: 4, name: 'Active Research', description: 'Comparison shopping, review reading, price checking' },
    { level: 3, name: 'Expressed Interest', description: 'Self-created event, forwarded link, reply chain' },
    { level: 2, name: 'Passive Engagement', description: 'Opened email, visited page, read article' },
    { level: 1, name: 'Ambient Exposure', description: 'Newsletter, notification, automated email' },
];
/**
 * Parse and validate Pass 1 structured LLM output.
 *
 * Returns validated signals or null if parsing fails (caller should fall back to raw text).
 */
export function parseAndValidatePass1(raw) {
    // Try to extract JSON
    let jsonStr;
    try {
        jsonStr = extractJSON(raw);
    }
    catch {
        return null;
    }
    // Parse JSON
    let parsed;
    try {
        parsed = JSON.parse(jsonStr);
    }
    catch {
        return null;
    }
    // Validate
    const result = Pass1ResponseSchema.safeParse(parsed);
    if (result.success) {
        return {
            signals: result.data.signals,
            underrepresentedSignals: result.data.underrepresentedSignals,
            errors: [],
        };
    }
    // Partial recovery: validate signals individually
    const obj = parsed;
    const rawSignals = Array.isArray(obj.signals) ? obj.signals : [];
    const errors = [];
    const validSignals = [];
    for (let i = 0; i < rawSignals.length; i++) {
        const signalResult = Pass1SignalSchema.safeParse(rawSignals[i]);
        if (signalResult.success) {
            validSignals.push(signalResult.data);
        }
        else {
            errors.push(`Signal ${i}: ${signalResult.error.issues.map(iss => iss.message).join(', ')}`);
        }
    }
    if (validSignals.length === 0) {
        return null; // Complete failure — fall back to raw text
    }
    return {
        signals: validSignals,
        underrepresentedSignals: typeof obj.underrepresentedSignals === 'string'
            ? obj.underrepresentedSignals
            : undefined,
        errors,
    };
}
/**
 * Format structured Pass 1 signals as a ranked table for Pass 2 input.
 * Sorted by intentLevel descending (highest intent first).
 */
export function formatSignalsForPass2(signals, underrepresentedSignals) {
    const sorted = [...signals].sort((a, b) => b.intentLevel - a.intentLevel);
    const lines = sorted.map(s => `[Intent ${s.intentLevel}] ${s.description} — ${s.intentReason} (entity: ${s.entity}, proxy: ${s.proxy})`);
    if (underrepresentedSignals) {
        lines.push('');
        lines.push(`NOTE: ${underrepresentedSignals}`);
    }
    return lines.join('\n');
}
//# sourceMappingURL=pass1-schema.js.map